import { useMemo } from 'react';
import { AreaChart, Area, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { TrendingUp, Filter, Calendar } from 'lucide-react';

interface ForecastDataPoint {
  timestamp: Date;
  predictedValue: number;
  actualValue?: number;
  upperBound?: number;
  lowerBound?: number;
  interval: string; // "9:00 AM", "9:15 AM", etc.
}

type ViewMode = 'DAILY' | 'WEEKLY' | 'MONTHLY';

interface ForecastVsActualChartProps {
  data: ForecastDataPoint[];
  interval: '15m' | '30m' | '60m';
  onIntervalChange: (interval: '15m' | '30m' | '60m') => void;
  selectedChannel: 'ALL' | 'PHONE' | 'CHAT' | 'EMAIL';
  onChannelChange: (channel: 'ALL' | 'PHONE' | 'CHAT' | 'EMAIL') => void;
  selectedTeam?: string;
  onTeamChange?: (team: string) => void;
  selectedShift?: string;
  onShiftChange?: (shift: string) => void;
  viewMode?: ViewMode;
  onViewModeChange?: (mode: ViewMode) => void;
  variancePercent?: number; // Variance percentage for confidence bands (default: 15)
  selectedDate?: Date;
  onDateChange?: (date: Date) => void;
  onPreviousDay?: () => void;
  onNextDay?: () => void;
  onToday?: () => void;
}

export default function ForecastVsActualChart({
  data,
  interval,
  onIntervalChange,
  selectedChannel,
  onChannelChange,
  selectedTeam = 'ALL',
  onTeamChange,
  selectedShift = 'ALL',
  onShiftChange,
  viewMode = 'DAILY',
  onViewModeChange,
  variancePercent = 15,
  selectedDate,
  onDateChange,
  onPreviousDay,
  onNextDay,
  onToday,
}: ForecastVsActualChartProps) {
  // Available teams (in production, fetch from API)
  const teams = ['ALL', 'Technical Support', 'Phone Support', 'Live Chat', 'Email Support'];
  const shifts = ['ALL', 'Morning (6AM-2PM)', 'Day (9AM-5PM)', 'Evening (2PM-10PM)', 'Night (10PM-6AM)'];
  // Aggregate data based on selected interval
  // For WEEKLY/MONTHLY views, the data is already aggregated by day from the parent component,
  // so we skip the time-based aggregation to preserve daily data points
  const aggregatedData = useMemo(() => {
    console.log(`[ForecastChart] viewMode=${viewMode}, received ${data?.length || 0} data points`);
    if (data && data.length > 0) {
      console.log(`[ForecastChart] First point: interval="${data[0].interval}", timestamp=${data[0].timestamp}`);
      console.log(`[ForecastChart] Last point: interval="${data[data.length-1].interval}"`);
    }

    if (!data || data.length === 0) return [];

    // For WEEKLY/MONTHLY views, data is already aggregated by day - return as-is
    // The parent component has already grouped by date, so we don't want to re-group by time
    if (viewMode === 'WEEKLY' || viewMode === 'MONTHLY') {
      console.log(`[ForecastChart] WEEKLY/MONTHLY mode - returning ${data.length} points as-is`);
      return data;
    }

    // For DAILY view with 15m interval, return data as-is
    if (interval === '15m') return data;

    // For DAILY view with 30m or 60m interval, aggregate by time of day
    const intervalMinutes = interval === '30m' ? 30 : 60;
    const grouped: { [key: string]: ForecastDataPoint[] } = {};

    data.forEach((point) => {
      // Ensure timestamp is a Date object
      const timestamp = point.timestamp instanceof Date
        ? point.timestamp
        : new Date(point.timestamp);

      // Skip invalid timestamps
      if (isNaN(timestamp.getTime())) {
        console.warn('Invalid timestamp in forecast data point:', point);
        return;
      }

      const hour = timestamp.getHours();
      const minute = timestamp.getMinutes();
      const roundedMinute = Math.floor(minute / intervalMinutes) * intervalMinutes;
      const key = `${hour}:${roundedMinute.toString().padStart(2, '0')}`;

      if (!grouped[key]) grouped[key] = [];
      grouped[key].push({ ...point, timestamp }); // Store with converted timestamp
    });

    return Object.entries(grouped).map(([key, points]) => {
      const avgPredicted = points.reduce((sum, p) => sum + p.predictedValue, 0) / points.length;
      const pointsWithActual = points.filter(p => p.actualValue != null);
      const avgActual = pointsWithActual.length > 0
        ? pointsWithActual.reduce((sum, p) => sum + (p.actualValue || 0), 0) / pointsWithActual.length
        : undefined;
      const pointsWithUpper = points.filter(p => p.upperBound != null);
      const avgUpper = pointsWithUpper.length > 0
        ? pointsWithUpper.reduce((sum, p) => sum + (p.upperBound || 0), 0) / pointsWithUpper.length
        : undefined;
      const pointsWithLower = points.filter(p => p.lowerBound != null);
      const avgLower = pointsWithLower.length > 0
        ? pointsWithLower.reduce((sum, p) => sum + (p.lowerBound || 0), 0) / pointsWithLower.length
        : undefined;

      return {
        timestamp: points[0].timestamp,
        predictedValue: Math.round(avgPredicted),
        actualValue: avgActual != null ? Math.round(avgActual) : undefined,
        upperBound: avgUpper != null ? Math.round(avgUpper) : undefined,
        lowerBound: avgLower != null ? Math.round(avgLower) : undefined,
        interval: key,
      };
    });
  }, [data, interval, viewMode]);

  // Format data for Recharts with variance bands
  const chartData = useMemo(() => {
    return aggregatedData.map((point) => {
      // Calculate variance bands if not provided
      const upper = point.upperBound || Math.round(point.predictedValue * (1 + variancePercent / 100));
      const lower = point.lowerBound || Math.round(point.predictedValue * (1 - variancePercent / 100));

      return {
        time: point.interval,
        predicted: point.predictedValue,
        actual: point.actualValue,
        upper,
        lower,
        // For area chart range
        range: [lower, upper],
      };
    });
  }, [aggregatedData, variancePercent]);

  // Calculate summary stats
  const stats = useMemo(() => {
    const withActuals = aggregatedData.filter(d => d.actualValue);
    if (withActuals.length === 0) return null;

    const totalPredicted = withActuals.reduce((sum, d) => sum + d.predictedValue, 0);
    const totalActual = withActuals.reduce((sum, d) => sum + (d.actualValue || 0), 0);
    const variance = ((totalActual - totalPredicted) / totalPredicted) * 100;
    const accuracy = 100 - Math.abs(variance);

    return {
      totalPredicted: Math.round(totalPredicted),
      totalActual: Math.round(totalActual),
      variance: variance.toFixed(1),
      accuracy: accuracy.toFixed(1),
    };
  }, [aggregatedData]);

  // Custom tooltip
  const CustomTooltip = ({ active, payload }: any) => {
    if (!active || !payload || !payload.length) return null;

    const data = payload[0].payload;

    return (
      <div className="bg-gray-900 text-white px-4 py-3 rounded-lg shadow-lg border border-gray-700">
        <div className="font-bold text-sm mb-2">{data.time}</div>
        <div className="space-y-1 text-xs">
          <div className="flex items-center justify-between">
            <span className="text-purple-300 flex items-center">
              <span className="w-3 h-3 bg-purple-500 rounded-full mr-2"></span>
              Predicted:
            </span>
            <span className="font-semibold ml-3">{data.predicted}</span>
          </div>
          {data.actual && (
            <div className="flex items-center justify-between">
              <span className="text-cyan-300 flex items-center">
                <span className="w-3 h-3 bg-cyan-500 rounded-full mr-2"></span>
                Actual:
              </span>
              <span className="font-semibold ml-3">{data.actual}</span>
            </div>
          )}
          {data.range && (
            <div className="flex items-center justify-between text-gray-400">
              <span>Range:</span>
              <span className="font-semibold ml-3">{data.lower} - {data.upper}</span>
            </div>
          )}
          {data.actual && (
            <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-700">
              <span>Variance:</span>
              <span className={`font-semibold ml-3 ${
                Math.abs(data.actual - data.predicted) < data.predicted * 0.1
                  ? 'text-green-400'
                  : 'text-yellow-400'
              }`}>
                {((data.actual - data.predicted) / data.predicted * 100).toFixed(1)}%
              </span>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <TrendingUp className="h-5 w-5 text-purple-600" />
          <h2 className="text-lg font-semibold text-gray-900">Forecast vs Actual Volume</h2>
        </div>

        <div className="flex items-center space-x-3">
          {/* Date Navigation */}
          {selectedDate && onDateChange && onPreviousDay && onNextDay && onToday && (
            <div className="flex items-center gap-1.5">
              <Calendar className="h-4 w-4 text-gray-500" />
              <button
                onClick={onPreviousDay}
                className="p-1.5 text-gray-700 hover:bg-gray-100 rounded"
                title="Previous day"
              >
                ←
              </button>

              <input
                type="date"
                value={selectedDate.toISOString().split('T')[0]}
                onChange={(e) => onDateChange(new Date(e.target.value))}
                className="px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-purple-500"
              />

              <button
                onClick={onToday}
                className="px-2 py-1 text-xs font-medium text-gray-700 bg-gray-100 rounded hover:bg-gray-200"
              >
                Today
              </button>

              <button
                onClick={onNextDay}
                className="p-1.5 text-gray-700 hover:bg-gray-100 rounded"
                title="Next day"
              >
                →
              </button>
            </div>
          )}

          {/* View Mode Toggle (Daily/Weekly/Monthly) */}
          {onViewModeChange && (
            <div className="flex items-center bg-gray-100 rounded-md p-1">
              {(['DAILY', 'WEEKLY', 'MONTHLY'] as const).map((mode) => (
                <button
                  key={mode}
                  onClick={() => onViewModeChange(mode)}
                  className={`px-3 py-1.5 text-sm font-medium rounded transition-colors ${
                    viewMode === mode
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {mode.charAt(0) + mode.slice(1).toLowerCase()}
                </button>
              ))}
            </div>
          )}

          {/* Interval Toggle */}
          <div className="flex items-center bg-gray-100 rounded-md p-1">
            {(['15m', '30m', '60m'] as const).map((int) => (
              <button
                key={int}
                onClick={() => onIntervalChange(int)}
                className={`px-3 py-1.5 text-sm font-medium rounded transition-colors ${
                  interval === int
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {int}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Filters Row */}
      <div className="flex items-center space-x-3 mb-6 pb-4 border-b border-gray-200">
        <Filter className="h-4 w-4 text-gray-500" />

        {/* Channel Filter */}
        <select
          value={selectedChannel}
          onChange={(e) => onChannelChange(e.target.value as any)}
          className="px-3 py-1.5 text-sm font-medium border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
        >
          <option value="ALL">All Channels</option>
          <option value="PHONE">Phone</option>
          <option value="CHAT">Chat</option>
          <option value="EMAIL">Email</option>
        </select>

        {/* Team Filter */}
        {onTeamChange && (
          <select
            value={selectedTeam}
            onChange={(e) => onTeamChange(e.target.value)}
            className="px-3 py-1.5 text-sm font-medium border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
          >
            {teams.map((team) => (
              <option key={team} value={team}>
                {team === 'ALL' ? 'All Teams' : team}
              </option>
            ))}
          </select>
        )}

        {/* Shift Filter */}
        {onShiftChange && (
          <select
            value={selectedShift}
            onChange={(e) => onShiftChange(e.target.value)}
            className="px-3 py-1.5 text-sm font-medium border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
          >
            {shifts.map((shift) => (
              <option key={shift} value={shift}>
                {shift === 'ALL' ? 'All Shifts' : shift}
              </option>
            ))}
          </select>
        )}

        {/* Active filters badge */}
        {(selectedChannel !== 'ALL' || selectedTeam !== 'ALL' || selectedShift !== 'ALL') && (
          <span className="text-xs px-2 py-1 bg-purple-100 text-purple-700 rounded-full">
            Filters active
          </span>
        )}
      </div>

      {/* Summary Stats */}
      {stats && (
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="bg-purple-50 rounded-lg p-3 border border-purple-200">
            <div className="text-xs font-medium text-purple-700 mb-1">Predicted Total</div>
            <div className="text-2xl font-bold text-purple-900">{stats.totalPredicted.toLocaleString()}</div>
          </div>
          <div className="bg-cyan-50 rounded-lg p-3 border border-cyan-200">
            <div className="text-xs font-medium text-cyan-700 mb-1">Actual Total</div>
            <div className="text-2xl font-bold text-cyan-900">{stats.totalActual.toLocaleString()}</div>
          </div>
          <div className={`rounded-lg p-3 border ${
            Math.abs(parseFloat(stats.variance)) < 10
              ? 'bg-green-50 border-green-200'
              : 'bg-yellow-50 border-yellow-200'
          }`}>
            <div className={`text-xs font-medium mb-1 ${
              Math.abs(parseFloat(stats.variance)) < 10 ? 'text-green-700' : 'text-yellow-700'
            }`}>
              Variance
            </div>
            <div className={`text-2xl font-bold ${
              Math.abs(parseFloat(stats.variance)) < 10 ? 'text-green-900' : 'text-yellow-900'
            }`}>
              {stats.variance}%
            </div>
          </div>
          <div className="bg-indigo-50 rounded-lg p-3 border border-indigo-200">
            <div className="text-xs font-medium text-indigo-700 mb-1">Accuracy</div>
            <div className="text-2xl font-bold text-indigo-900">{stats.accuracy}%</div>
          </div>
        </div>
      )}

      {/* Chart */}
      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
            <defs>
              {/* Confidence range gradient */}
              <linearGradient id="confidenceGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#9ca3af" stopOpacity={0.1} />
                <stop offset="95%" stopColor="#9ca3af" stopOpacity={0.05} />
              </linearGradient>
              {/* Predicted area gradient */}
              <linearGradient id="predictedGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0.05} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis
              dataKey="time"
              stroke="#6b7280"
              style={{ fontSize: '12px' }}
              tick={{ fill: '#6b7280' }}
            />
            <YAxis
              stroke="#6b7280"
              style={{ fontSize: '12px' }}
              tick={{ fill: '#6b7280' }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend
              wrapperStyle={{ fontSize: '14px', paddingTop: '20px' }}
              iconType="circle"
            />

            {/* Confidence Range (Light Gray Shadow) */}
            {chartData[0]?.range && (
              <Area
                type="monotone"
                dataKey="upper"
                stroke="none"
                fill="url(#confidenceGradient)"
                name="Confidence Range"
                isAnimationActive={false}
              />
            )}
            {chartData[0]?.range && (
              <Area
                type="monotone"
                dataKey="lower"
                stroke="none"
                fill="#ffffff"
                name=""
                isAnimationActive={false}
              />
            )}

            {/* Predicted Volume (Purple Area) */}
            <Area
              type="monotone"
              dataKey="predicted"
              stroke="#8b5cf6"
              strokeWidth={3}
              fill="url(#predictedGradient)"
              name="Predicted Volume"
            />

            {/* Actual Volume (Cyan Line) */}
            {chartData.some(d => d.actual) && (
              <Line
                type="monotone"
                dataKey="actual"
                stroke="#06b6d4"
                strokeWidth={2}
                dot={{ fill: '#06b6d4', r: 3 }}
                name="Actual Volume"
              />
            )}
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Legend Note */}
      <div className="mt-4 text-xs text-gray-500 flex items-center justify-center">
        <span className="mr-4">💡 Gray shaded area represents forecast confidence interval</span>
        {!stats && <span className="text-yellow-600">⚠️ No actual data available yet</span>}
      </div>
    </div>
  );
}
